import { itemListFormulaValue } from '../utils/item-list.util';

export type FormulaOperator = '+' | '-' | '*' | '/';

/**
 * Alinhado com `FormulaOperatorToNext` no backend (.NET).
 * Confirmado: None = 0, Subtract = 2. Add/Multiply/Divide seguem ordem usual (1,3,4).
 */
export const FORMULA_OPERATOR_TO_NEXT_API = {
  None: 0,
  Add: 1,
  Subtract: 2,
  Multiply: 3,
  Divide: 4,
} as const;

/** Alinhado com o enum de tipo de operando no backend: 1 = CustomField, 2 = Constant. */
export const FORMULA_OPERAND_TYPE_API = {
  CustomField: 1,
  Constant: 2,
} as const;

export interface FormulaOperandApiPayload {
  operandType: number;
  sourceCustomFieldId: string | null;
  constantValue: string | null;
  operatorToNext: number;
}

export function formulaOperatorToApiValue(operator: FormulaOperator): number {
  switch (operator) {
    case '+':
      return FORMULA_OPERATOR_TO_NEXT_API.Add;
    case '-':
      return FORMULA_OPERATOR_TO_NEXT_API.Subtract;
    case '*':
      return FORMULA_OPERATOR_TO_NEXT_API.Multiply;
    case '/':
      return FORMULA_OPERATOR_TO_NEXT_API.Divide;
    default:
      return FORMULA_OPERATOR_TO_NEXT_API.None;
  }
}

/** Corpo `formulaOperands` esperado pela API (camelCase). */
export function toFormulaOperandsApiPayload(def: FormulaDefinition): FormulaOperandApiPayload[] {
  const n = def.operands.length;
  return def.operands.map((op, i) => {
    const isLast = i === n - 1;
    const operatorToNext = isLast
      ? FORMULA_OPERATOR_TO_NEXT_API.None
      : formulaOperatorToApiValue(def.operators[i] ?? '+');

    if (op.kind === 'field') {
      return {
        operandType: FORMULA_OPERAND_TYPE_API.CustomField,
        sourceCustomFieldId: op.fieldId,
        constantValue: null,
        operatorToNext,
      };
    }
    return {
      operandType: FORMULA_OPERAND_TYPE_API.Constant,
      sourceCustomFieldId: null,
      constantValue: op.constantRaw.trim() ? op.constantRaw.trim() : null,
      operatorToNext,
    };
  });
}

export interface FormulaOperand {
  id: string;
  kind: 'field' | 'constant';
  fieldId: string | null;
  constantRaw: string;
}

export interface FormulaDefinition {
  operands: FormulaOperand[];
  /** operators[i] applies between operands[i] and operands[i + 1] */
  operators: FormulaOperator[];
}

export const MAX_FORMULA_PARTS = 5;

export type FormulaValidationErrorCode =
  | 'missing_definition'
  | 'too_few_parts'
  | 'too_many_parts'
  | 'missing_operator'
  | 'invalid_operator'
  | 'missing_field'
  | 'invalid_field'
  | 'invalid_constant'
  | 'division_by_zero';

export interface FormulaDefinitionPersisted {
  version: 1;
  operands: Array<{
    id?: string;
    kind: 'field' | 'constant';
    fieldId?: string | null;
    constant?: string | null;
  }>;
  operators: FormulaOperator[];
}

let operandSeq = 0;

export function nextOperandId(): string {
  operandSeq += 1;
  return `fo-${operandSeq}-${Date.now().toString(36)}`;
}

export function createEmptyOperand(kind: FormulaOperand['kind'] = 'field'): FormulaOperand {
  return {
    id: nextOperandId(),
    kind,
    fieldId: null,
    constantRaw: '',
  };
}

export function createDefaultFormulaDefinition(): FormulaDefinition {
  return {
    operands: [createEmptyOperand('field'), createEmptyOperand('field')],
    operators: ['*'],
  };
}

function coerceGuidLikeString(value: unknown): string | null {
  if (value == null) return null;
  if (typeof value === 'string') {
    const t = value.trim();
    return t || null;
  }
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  const s = String(value).trim();
  return s || null;
}

/** Lê o id do workflow field referenciado pelo operando (vários nomes usados pela API / serialização). */
export function pickOperandSourceFieldId(o: Record<string, unknown>): string | null {
  const keys = [
    'fieldId',
    'FieldId',
    'sourceCustomFieldId',
    'SourceCustomFieldId',
    'workflowFieldId',
    'WorkflowFieldId',
    'customFieldId',
    'CustomFieldId',
    'sourceWorkflowFieldId',
    'SourceWorkflowFieldId',
  ];
  for (const k of keys) {
    const id = coerceGuidLikeString(o[k]);
    if (id) return id;
  }
  return null;
}

/** True se a definição não tem referências de campo preenchidas (precisa de formulaOperands da API). */
export function formulaDefinitionNeedsApiOperands(def: FormulaDefinition | null): boolean {
  if (!def || def.operands.length < 2) return true;
  if (def.operators.length !== def.operands.length - 1) return true;
  return def.operands.some((op) => op.kind === 'field' && !op.fieldId);
}

export function parseFormulaDefinition(raw: unknown): FormulaDefinition | null {
  if (raw == null) return null;
  let parsed: unknown = raw;
  if (typeof raw === 'string') {
    const t = raw.trim();
    if (!t) return null;
    try {
      parsed = JSON.parse(t) as unknown;
    } catch {
      return null;
    }
  }
  if (!parsed || typeof parsed !== 'object') return null;
  const rec = parsed as Record<string, unknown>;
  const ops = rec['operands'];
  const opers = rec['operators'];
  if (!Array.isArray(ops) || ops.length < 2) return null;
  if (!Array.isArray(opers) || opers.length !== ops.length - 1) return null;

  const operands: FormulaOperand[] = ops.map((item) => {
    const o = item && typeof item === 'object' ? (item as Record<string, unknown>) : {};
    const kind = o['kind'] === 'constant' ? 'constant' : 'field';
    const fieldId = kind === 'field' ? pickOperandSourceFieldId(o) : null;
    return {
      id: typeof o['id'] === 'string' && o['id'] ? o['id'] : nextOperandId(),
      kind,
      fieldId,
      constantRaw: typeof o['constant'] === 'string' ? o['constant'] : String(o['constant'] ?? ''),
    };
  });

  const operators: FormulaOperator[] = opers.map((x) =>
    x === '+' || x === '-' || x === '*' || x === '/' ? x : '*'
  );

  return { operands, operators };
}

/** Converte `operatorToNext` da API para o operador entre este operando e o próximo. */
export function apiOperatorToFormulaOperator(code: number): FormulaOperator {
  switch (code) {
    case FORMULA_OPERATOR_TO_NEXT_API.Add:
      return '+';
    case FORMULA_OPERATOR_TO_NEXT_API.Subtract:
      return '-';
    case FORMULA_OPERATOR_TO_NEXT_API.Multiply:
      return '*';
    case FORMULA_OPERATOR_TO_NEXT_API.Divide:
      return '/';
    default:
      return '+';
  }
}

function isConstantOperandType(operandType: number): boolean {
  return operandType === FORMULA_OPERAND_TYPE_API.Constant || operandType === 2;
}

/**
 * Contrato GET: `formula: { resultType, operands: [...] }` (camelCase).
 * Ordena operandos por `order` e reutiliza o parse do array (mesma forma de cada item).
 */
export function parseFormulaDefinitionFromContractFormula(raw: unknown): FormulaDefinition | null {
  if (raw == null) return null;
  const rec =
    raw && typeof raw === 'object' && !Array.isArray(raw) ? (raw as Record<string, unknown>) : null;
  if (!rec) return null;
  const opsRaw = rec['operands'] ?? rec['Operands'];
  if (!Array.isArray(opsRaw) || opsRaw.length < 2) return null;
  const sorted = [...opsRaw].sort((a, b) => {
    const ra = a && typeof a === 'object' ? (a as Record<string, unknown>) : {};
    const rb = b && typeof b === 'object' ? (b as Record<string, unknown>) : {};
    const oa = Number(ra['order'] ?? ra['Order'] ?? 0);
    const ob = Number(rb['order'] ?? rb['Order'] ?? 0);
    return oa - ob;
  });
  return parseFormulaDefinitionFromApiOperands(sorted);
}

/**
 * Monta `FormulaDefinition` a partir do array `formulaOperands` retornado pela API (.NET),
 * quando `formulaJson` não vem ou não bate com o formato do front.
 */
export function parseFormulaDefinitionFromApiOperands(raw: unknown): FormulaDefinition | null {
  if (!Array.isArray(raw) || raw.length < 2) return null;

  const operands: FormulaOperand[] = [];
  const operators: FormulaOperator[] = [];

  for (let i = 0; i < raw.length; i += 1) {
    const row = raw[i];
    if (!row || typeof row !== 'object') return null;
    const rec = row as Record<string, unknown>;

    const operandType = Number(rec['operandType'] ?? rec['OperandType'] ?? 1);
    const isConstant = isConstantOperandType(operandType);
    const sourceId = isConstant ? null : pickOperandSourceFieldId(rec);

    const constantRaw = rec['constantValue'] ?? rec['ConstantValue'];
    const constantStr =
      constantRaw == null
        ? ''
        : typeof constantRaw === 'string'
          ? constantRaw
          : String(constantRaw);

    const opToNext = Number(rec['operatorToNext'] ?? rec['OperatorToNext'] ?? 0);

    operands.push({
      id: nextOperandId(),
      kind: isConstant ? 'constant' : 'field',
      fieldId: isConstant ? null : sourceId,
      constantRaw: isConstant ? constantStr : '',
    });

    if (i < raw.length - 1) {
      operators.push(apiOperatorToFormulaOperator(opToNext));
    }
  }

  if (operands.length < 2 || operators.length !== operands.length - 1) return null;
  return { operands, operators };
}

function bareGuidKey(id: string): string {
  return id.replace(/^\{|\}$/g, '').trim().toLowerCase();
}

/** Corrige casing / chaves de GUID do operando para bater com `fieldId` dos campos do workflow (select). */
export function alignFormulaFieldIdsToWorkflow(
  def: FormulaDefinition,
  workflowFieldIds: Iterable<string>
): FormulaDefinition {
  const idList = [...workflowFieldIds];
  const bareToCanonical = new Map<string, string>();
  for (const id of idList) {
    bareToCanonical.set(bareGuidKey(id), id);
  }
  return {
    operands: def.operands.map((o) => {
      if (o.kind !== 'field' || !o.fieldId) return o;
      if (idList.includes(o.fieldId)) return { ...o };
      const canon = bareToCanonical.get(bareGuidKey(o.fieldId));
      return canon ? { ...o, fieldId: canon } : { ...o };
    }),
    operators: [...def.operators],
  };
}

export function toPersistedFormula(def: FormulaDefinition): FormulaDefinitionPersisted {
  return {
    version: 1,
    operands: def.operands.map((o) => ({
      id: o.id,
      kind: o.kind,
      fieldId: o.fieldId,
      constant: o.kind === 'constant' ? o.constantRaw : null,
    })),
    operators: [...def.operators],
  };
}

export function isConstantNumeric(raw: string): boolean {
  const t = raw.trim().replace(',', '.');
  if (!t) return false;
  return Number.isFinite(Number(t));
}

export function validateFormulaDefinitionDetailed(
  def: FormulaDefinition | null | undefined,
  allowedFieldIds: Set<string>,
  maxParts = MAX_FORMULA_PARTS
): FormulaValidationErrorCode | null {
  if (!def) return 'missing_definition';
  if (def.operands.length < 2) return 'too_few_parts';
  if (def.operands.length > maxParts) return 'too_many_parts';
  if (def.operators.length !== def.operands.length - 1) return 'missing_operator';
  for (let i = 0; i < def.operands.length; i += 1) {
    const op = def.operands[i];
    if (op.kind === 'field') {
      if (!op.fieldId) return 'missing_field';
      if (!allowedFieldIds.has(op.fieldId)) return 'invalid_field';
    } else if (!isConstantNumeric(op.constantRaw)) {
      return 'invalid_constant';
    }
    if (i < def.operators.length) {
      const o = def.operators[i];
      if (o !== '+' && o !== '-' && o !== '*' && o !== '/') return 'invalid_operator';
      if (o === '/' && i + 1 < def.operands.length) {
        const nextOperand = def.operands[i + 1];
        if (nextOperand.kind === 'constant') {
          const v = Number(nextOperand.constantRaw.trim().replace(',', '.'));
          if (Number.isFinite(v) && v === 0) return 'division_by_zero';
        }
      }
    }
  }
  return null;
}

export function validateFormulaDefinition(
  def: FormulaDefinition | null | undefined,
  allowedFieldIds: Set<string>,
  maxParts = MAX_FORMULA_PARTS
): boolean {
  return validateFormulaDefinitionDetailed(def, allowedFieldIds, maxParts) === null;
}

export function buildFormulaPreview(
  def: FormulaDefinition | null | undefined,
  fieldNameById: Map<string, string>
): string {
  if (!def?.operands.length) return '';
  const labels: string[] = [];
  for (let i = 0; i < def.operands.length; i += 1) {
    const op = def.operands[i];
    const label =
      op.kind === 'field'
        ? (op.fieldId ? fieldNameById.get(op.fieldId) ?? '?' : '?')
        : op.constantRaw.trim() || '?';
    labels.push(label);
  }
  let preview = labels[0] ?? '';
  for (let i = 0; i < def.operators.length && i + 1 < labels.length; i += 1) {
    const operator = def.operators[i] ?? '+';
    const next = labels[i + 1] ?? '?';
    preview = `(${preview} ${operator} ${next})`;
  }
  return preview;
}

function parseFormulaOperandNumber(raw: unknown): number | null {
  if (raw == null) return null;
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
  if (typeof raw === 'boolean') return raw ? 1 : 0;
  if (typeof raw === 'string') {
    const t = raw.trim();
    if (!t) return null;
    if (t.startsWith('{')) return itemListFormulaValue(t);
    const direct = Number(t);
    if (Number.isFinite(direct)) return direct;
    // Aceita strings monetárias e variações pt-BR/en-US (ex.: "R$ 16.877,19", "1,234.56", "12,5").
    const clean = t.replace(/[^\d,.\-+]/g, '');
    if (!clean) return null;
    const hasDot = clean.includes('.');
    const hasComma = clean.includes(',');
    let normalized = clean;
    if (hasDot && hasComma) {
      const lastDot = clean.lastIndexOf('.');
      const lastComma = clean.lastIndexOf(',');
      if (lastComma > lastDot) {
        // Formato pt-BR com milhar em ".".
        normalized = clean.replace(/\./g, '').replace(',', '.');
      } else {
        // Formato en-US com milhar em ",".
        normalized = clean.replace(/,/g, '');
      }
    } else if (hasComma) {
      normalized = clean.replace(',', '.');
    }
    const br = normalized;
    const n = Number(br);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function applyFormulaOperator(a: number, op: FormulaOperator, b: number): number | null {
  switch (op) {
    case '+':
      return a + b;
    case '-':
      return a - b;
    case '*':
      return a * b;
    case '/':
      return b === 0 ? null : a / b;
    default:
      return null;
  }
}

/**
 * Pré-visualização numérica no formulário (ex.: criar card), antes do backend recalcular.
 * Mesma associação à esquerda que o preview textual (op0 op1 op2 ...).
 */
export function evaluateFormulaNumericPreview(
  def: FormulaDefinition | null | undefined,
  getFieldValue: (fieldId: string) => unknown
): number | null {
  if (!def || def.operands.length < 2) return null;
  if (def.operators.length !== def.operands.length - 1) return null;

  let acc: number | null = null;

  for (let i = 0; i < def.operands.length; i += 1) {
    const operand = def.operands[i];
    const term =
      operand.kind === 'constant'
        ? parseFormulaOperandNumber(operand.constantRaw)
        : operand.fieldId
          ? parseFormulaOperandNumber(getFieldValue(operand.fieldId))
          : null;
    if (term == null) return null;

    if (i === 0) {
      acc = term;
    } else {
      const op = def.operators[i - 1];
      if (acc == null) return null;
      acc = applyFormulaOperator(acc, op, term);
      if (acc == null) return null;
    }
  }

  return acc;
}

/** API: 0–4 casas decimais no resultado gravado/exibido da fórmula. */
export function clampFormulaResultDecimalPlaces(n: number | null | undefined): number {
  if (n == null || !Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(4, Math.trunc(n)));
}

/**
 * Formata valor de campo fórmula conforme `resultDecimalPlaces` (alinhado ao backend após recálculo).
 */
export function formatFormulaResultForDisplay(
  value: string | number | boolean | string[] | File | File[] | null,
  decimalPlaces: number
): string {
  const places = clampFormulaResultDecimalPlaces(decimalPlaces);
  if (value == null || value === '') return '';
  if (value instanceof File) return value.name || '';
  if (typeof value === 'boolean') return value ? 'Sim' : 'Não';
  if (Array.isArray(value)) {
    if (value.some((x) => x instanceof File)) return '';
    return value.map((x) => String(x)).join(', ');
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    const fromNumber = roundDecimalForDisplay(numberToPlainDecimalString(value), places);
    if (fromNumber) return fromNumber;
    return places === 0 ? String(Math.trunc(value)) : value.toFixed(places);
  }
  if (typeof value === 'string') {
    const t = value.trim();
    if (!t) return '';
    const rounded = roundDecimalForDisplay(t, places);
    if (rounded) return rounded;
    return t;
  }
  return String(value);
}

function numberToPlainDecimalString(value: number): string {
  // Evita notação científica para arredondamento determinístico por string.
  return value.toLocaleString('en-US', {
    useGrouping: false,
    maximumFractionDigits: 20,
  });
}

function roundDecimalForDisplay(raw: string, decimalPlaces: number): string | null {
  const normalized = normalizeDecimalInput(raw);
  if (!normalized) return null;
  return roundNormalizedDecimal(normalized, decimalPlaces);
}

function normalizeDecimalInput(raw: string): string | null {
  const t = raw.trim().replace(/\s+/g, '');
  if (!t) return null;

  let normalized = t;
  const hasDot = normalized.includes('.');
  const hasComma = normalized.includes(',');
  if (hasDot && hasComma) {
    // pt-BR com milhar: 1.234,56 -> 1234.56
    normalized = normalized.replace(/\./g, '').replace(',', '.');
  } else if (hasComma) {
    normalized = normalized.replace(',', '.');
  }

  // Aceita somente decimal simples com sinal opcional.
  if (!/^[+-]?\d+(?:\.\d+)?$/.test(normalized)) return null;
  return normalized;
}

function roundNormalizedDecimal(value: string, decimalPlaces: number): string {
  const places = clampFormulaResultDecimalPlaces(decimalPlaces);
  const negative = value.startsWith('-');
  const unsigned = value.replace(/^[+-]/, '');
  const [intPartRaw, fracPartRaw = ''] = unsigned.split('.');
  const intPart = intPartRaw.replace(/^0+(?=\d)/, '') || '0';
  const fracPart = fracPartRaw.replace(/\D/g, '');

  if (places === 0) {
    let intAbs = BigInt(intPart);
    const shouldRoundUp = fracPart.length > 0 && fracPart[0] >= '5';
    if (shouldRoundUp) intAbs += 1n;
    return `${negative ? '-' : ''}${intAbs.toString()}`;
  }

  const baseFrac = fracPart.slice(0, places).padEnd(places, '0');
  const nextDigit = fracPart.length > places ? fracPart[places] ?? '0' : '0';
  const shouldRoundUp = nextDigit >= '5';

  let merged = BigInt(`${intPart}${baseFrac}`);
  if (shouldRoundUp) merged += 1n;

  const mergedStr = merged.toString().padStart(places + 1, '0');
  const cut = mergedStr.length - places;
  const outInt = mergedStr.slice(0, cut);
  const outFrac = mergedStr.slice(cut);
  return `${negative ? '-' : ''}${outInt}.${outFrac}`;
}
